import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Platform, TextInput, ActivityIndicator, useWindowDimensions } from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { useAppContext } from '../contexts/AppContext';
import { apiService } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

const CAMPAIGN_STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  failed: { label: 'Failed', color: '#EF4444', icon: 'close-circle' },
  pending: { label: 'Pending', color: '#F59E0B', icon: 'time-outline' },
  sent: { label: 'Sent', color: '#3B82F6', icon: 'checkmark-outline' },
  delivered: { label: 'Delivered', color: '#8B5CF6', icon: 'checkmark-done' },
  read: { label: 'Read', color: '#10B981', icon: 'eye-outline' },
  replied: { label: 'Replied', color: '#06B6D4', icon: 'chatbubble-outline' },
  handled: { label: 'Handled', color: '#6B7280', icon: 'shield-checkmark-outline' },
};

function formatDate(dateString?: string) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
}

function getTaskExecutionStatus(task: any): { status: 'pending' | 'executed' | 'scheduled' | 'failed'; text: string; isExecuted: boolean; isScheduled: boolean } {
  const isExecuted = !!(task.lastExecutedAt || task.executionCount > 0);
  const isScheduled = !!(task.nextExecutionAt && new Date(task.nextExecutionAt) > new Date());

  if (task.status === 0) return { status: 'pending', text: 'Deleted', isExecuted, isScheduled };
  if (task.status === 2) return { status: 'pending', text: 'Paused', isExecuted, isScheduled };
  if (task.status === 3) return { status: 'failed', text: 'Failed', isExecuted, isScheduled };

  if (isScheduled) {
    const scheduleInfo = task.cronExpression
      ? `Scheduled (${task.cronExpression}): ${formatDate(task.nextExecutionAt)}`
      : `Scheduled: ${formatDate(task.nextExecutionAt)}`;
    const suffix = !isExecuted ? ' (not executed yet)' : '';
    return { status: 'scheduled', text: scheduleInfo + suffix, isExecuted, isScheduled };
  }

  if (isExecuted) {
    if (task.failureCount > 0 && task.failureCount === task.executionCount) {
      return { status: 'failed', text: `Executed (${task.failureCount} failed)`, isExecuted, isScheduled };
    }
    if (task.failureCount > 0) {
      return { status: 'executed', text: `Executed (${task.executionCount - task.failureCount} success, ${task.failureCount} failed)`, isExecuted, isScheduled };
    }
    return { status: 'executed', text: `Executed (${task.executionCount} success)`, isExecuted, isScheduled };
  }

  return { status: 'pending', text: 'Pending execution', isExecuted, isScheduled };
}

export interface CampaignDetailsModalProps {
  visible: boolean;
  campaign: any | null;
  onClose: () => void;
  /** Web two-pane width sizing — HomeScreen has left/right panes open by
   * default, other embedding contexts (e.g. TemplatePerformanceModal, which
   * stacks this modal on top of itself) don't; defaults to false so the
   * modal is full-width unless told otherwise. */
  accountForSidePanes?: boolean;
}

/** Full campaign/task detail sheet — task info, message stats, recipient
 * status review with bulk-send, execution history, and task actions
 * (stop/reschedule/clone/resend). Self-fetching: loads its own data from
 * `campaign.taskId`/`campaign.id` whenever it receives a new campaign while
 * visible. Extracted from HomeScreen.tsx so the same modal instance can be
 * reused by TemplatePerformanceModal.tsx's campaign drill-down. */
export const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({ visible, campaign, onClose, accountForSidePanes = false }) => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo } = useAppContext();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768 || screenHeight < 768);

  const [campaignDetails, setCampaignDetails] = useState<any | null>(null);
  const [executionHistory, setExecutionHistory] = useState<any[]>([]);
  const [loadingCampaignDetails, setLoadingCampaignDetails] = useState(false);
  const [campaignSelectedContacts, setCampaignSelectedContacts] = useState<Set<string>>(new Set());
  const [campaignActiveStatusTab, setCampaignActiveStatusTab] = useState<string>('all');
  const [campaignActionLoading, setCampaignActionLoading] = useState(false);
  const [showCampaignReschedule, setShowCampaignReschedule] = useState(false);
  const [campaignRescheduleDate, setCampaignRescheduleDate] = useState('');

  useEffect(() => {
    if (!visible || !campaign) return;

    const taskId = campaign.taskId || campaign.id;
    if (!taskId) {
      console.error('❌ [CampaignDetailsModal] Campaign task ID not found:', campaign);
      showNotification('error', 'Error', 'Campaign task ID not found');
      return;
    }

    let cancelled = false;
    setLoadingCampaignDetails(true);
    setCampaignSelectedContacts(new Set());
    setCampaignActiveStatusTab('all');
    setShowCampaignReschedule(false);
    setCampaignRescheduleDate('');
    setCampaignDetails(null);
    setExecutionHistory([]);

    (async () => {
      try {
        const [taskResponse, statsResponse, historyResponse] = await Promise.all([
          apiService.getTaskById(taskId),
          apiService.getTaskMessageStats(taskId).catch(() => null),
          apiService.getTaskExecutionHistory(taskId, 50).catch(() => null),
        ]);
        if (cancelled) return;

        if (taskResponse.statusCode === 1000 && taskResponse.data) {
          setCampaignDetails({
            task: taskResponse.data,
            stats: statsResponse?.statusCode === 1000 ? statsResponse.data : null,
          });
          setExecutionHistory(historyResponse?.statusCode === 1000 && historyResponse.data ? historyResponse.data : []);
        } else {
          showNotification('error', 'Error', 'Failed to load campaign details');
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('❌ [CampaignDetailsModal] Error loading campaign details:', error);
        showNotification('error', 'Error', error.message || 'Failed to load campaign details');
      } finally {
        if (!cancelled) setLoadingCampaignDetails(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, campaign?.taskId, campaign?.id]);

  const getCampaignAllContacts = () => {
    const cbs = campaignDetails?.stats?.contactsByStatus;
    if (!cbs) return [];
    const seen = new Set<string>();
    const all: Array<{ contactId: string; name?: string; phoneNumber?: string }> = [];
    Object.values(cbs).forEach((arr: any) => arr?.forEach((c: any) => {
      if (!seen.has(c.contactId)) { seen.add(c.contactId); all.push(c); }
    }));
    return all;
  };

  const getCampaignContactStatus = (contactId: string) => {
    const cbs = campaignDetails?.stats?.contactsByStatus;
    if (!cbs) return 'unknown';
    for (const [status, arr] of Object.entries(cbs)) {
      if ((arr as any[])?.some((c: any) => c.contactId === contactId)) return status;
    }
    return 'unknown';
  };

  const getCampaignContactsForTab = () => {
    const cbs = campaignDetails?.stats?.contactsByStatus;
    if (!cbs) return [];
    if (campaignActiveStatusTab === 'all') return getCampaignAllContacts();
    return (cbs[campaignActiveStatusTab] || []) as Array<{ contactId: string; name?: string; phoneNumber?: string }>;
  };

  const toggleCampaignContact = (contactId: string) => {
    setCampaignSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
      return next;
    });
  };

  const toggleCampaignSelectAll = () => {
    const visibleContacts = getCampaignContactsForTab();
    const allSel = visibleContacts.length > 0 && visibleContacts.every(c => campaignSelectedContacts.has(c.contactId));
    if (allSel) {
      setCampaignSelectedContacts(prev => { const n = new Set(prev); visibleContacts.forEach(c => n.delete(c.contactId)); return n; });
    } else {
      setCampaignSelectedContacts(prev => { const n = new Set(prev); visibleContacts.forEach(c => n.add(c.contactId)); return n; });
    }
  };

  const handleCampaignSendToSelected = () => {
    if (campaignSelectedContacts.size === 0) return;
    const bulk = getCampaignAllContacts()
      .filter(c => campaignSelectedContacts.has(c.contactId))
      .map(c => ({ name: c.name || '', phoneNumber: c.phoneNumber || '' }));
    if (bulk.length === 0) return;
    onClose();
    navigateTo('send-whatsapp-message', undefined, { bulkContacts: bulk });
  };

  const handleCampaignStop = async () => {
    const taskId = campaignDetails?.task?._id;
    if (!taskId) return;
    setCampaignActionLoading(true);
    try {
      const r = await apiService.deleteTask(taskId);
      if (r.statusCode === 1000) {
        showNotification('success', 'Task stopped', 'The scheduled task has been cancelled.');
        onClose();
      } else {
        showNotification('error', 'Error', r.errors?.[0]?.message || 'Failed to stop task');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to stop task');
    } finally {
      setCampaignActionLoading(false);
    }
  };

  const handleCampaignReschedule = async () => {
    const taskId = campaignDetails?.task?._id;
    if (!taskId || !campaignRescheduleDate) return;
    const date = new Date(campaignRescheduleDate);
    if (isNaN(date.getTime()) || date <= new Date()) {
      showNotification('error', 'Invalid date', 'Please pick a future date and time.'); return;
    }
    setCampaignActionLoading(true);
    try {
      const r = await apiService.updateTask(taskId, { nextExecutionAt: date.toISOString() });
      if (r.statusCode === 1000) {
        showNotification('success', 'Rescheduled', `Task will run at ${date.toLocaleString()}`);
        setShowCampaignReschedule(false);
        setCampaignRescheduleDate('');
      } else {
        showNotification('error', 'Error', r.errors?.[0]?.message || 'Failed to reschedule');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to reschedule');
    } finally {
      setCampaignActionLoading(false);
    }
  };

  const handleCampaignClone = (withContacts: boolean) => {
    const task = campaignDetails?.task;
    if (!task?.parameters) return;
    const { contacts, templateName, templateLanguage, message, parameters: params, providerId, headerAsset } = task.parameters;
    onClose();
    navigateTo('send-whatsapp-message', undefined, {
      ...(withContacts && contacts?.length ? { bulkContacts: contacts.map((c: any) => ({ name: c.name || '', phoneNumber: c.phoneNumber || c.phone || '' })) } : {}),
      cloneParams: { templateName, templateLanguage, message, parameters: params, providerId, headerAsset },
    });
  };

  const renderCampaignStatPills = (stats: any) => {
    const pills = [
      { key: 'totalSent', label: 'Total', value: stats.totalSent, color: colors.primary },
      { key: 'failed', label: 'Failed', value: stats.failed, color: '#EF4444' },
      { key: 'delivered', label: 'Delivered', value: stats.delivered, color: '#8B5CF6' },
      { key: 'read', label: 'Read', value: stats.read, color: '#10B981' },
      { key: 'pending', label: 'Pending', value: stats.pending, color: '#F59E0B' },
      { key: 'billable', label: 'Billable', value: stats.billableMessages, color: colors.primary },
    ];
    return (
      <View style={styles.campaignPillsRow}>
        {pills.map(p => (
          <View key={p.key} style={[styles.campaignStatPill, { borderColor: p.color as string }]}>
            <Text style={[styles.campaignStatPillValue, { color: p.color as string }]}>{p.value}</Text>
            <Text style={styles.campaignStatPillLabel}>{p.label}</Text>
          </View>
        ))}
      </View>
    );
  };

  const renderCampaignContactReview = () => {
    const cbs = campaignDetails?.stats?.contactsByStatus;
    if (!cbs) return null;
    const statusKeys = Object.keys(CAMPAIGN_STATUS_CONFIG).filter(k => (cbs[k]?.length ?? 0) > 0);
    if (statusKeys.length === 0) return null;
    const visibleContacts = getCampaignContactsForTab();
    const allVisibleSelected = visibleContacts.length > 0 && visibleContacts.every(c => campaignSelectedContacts.has(c.contactId));

    return (
      <View style={styles.campaignSection}>
        <View style={styles.campaignSectionHeader}>
          <Ionicons name="people-outline" size={20} color={colors.primary as string} />
          <Text style={styles.campaignSectionTitle}>Recipient Status</Text>
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: spacing.sm }}>
          <View style={styles.campaignStatusTabs}>
            <TouchableOpacity
              style={[styles.campaignStatusTab, campaignActiveStatusTab === 'all' && styles.campaignStatusTabActive]}
              onPress={() => setCampaignActiveStatusTab('all')}
            >
              <Text style={[styles.campaignStatusTabText, campaignActiveStatusTab === 'all' && styles.campaignStatusTabTextActive]}>
                All ({getCampaignAllContacts().length})
              </Text>
            </TouchableOpacity>
            {statusKeys.map(k => {
              const cfg = CAMPAIGN_STATUS_CONFIG[k];
              const count = cbs[k]?.length ?? 0;
              const isActive = campaignActiveStatusTab === k;
              return (
                <TouchableOpacity
                  key={k}
                  style={[styles.campaignStatusTab, isActive && { ...styles.campaignStatusTabActive, borderColor: cfg.color, backgroundColor: cfg.color + '20' }]}
                  onPress={() => setCampaignActiveStatusTab(k)}
                >
                  <Ionicons name={cfg.icon as any} size={12} color={isActive ? cfg.color : (colors.text.secondary as string)} />
                  <Text style={[styles.campaignStatusTabText, isActive && { color: cfg.color, fontFamily: typography.fontFamily.semibold }]}>
                    {cfg.label} ({count})
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>

        {visibleContacts.length > 0 && (
          <View style={styles.campaignContactActionBar}>
            <TouchableOpacity style={styles.campaignSelectAllRow} onPress={toggleCampaignSelectAll}>
              <View style={[styles.campaignCheckbox, allVisibleSelected && styles.campaignCheckboxChecked]}>
                {allVisibleSelected && <Ionicons name="checkmark" size={12} color={colors.white as string} />}
              </View>
              <Text style={styles.campaignSelectAllText}>{allVisibleSelected ? 'Deselect all' : 'Select all'} ({visibleContacts.length})</Text>
            </TouchableOpacity>
            {campaignSelectedContacts.size > 0 && (
              <TouchableOpacity style={styles.campaignSendSelectedBtn} onPress={handleCampaignSendToSelected}>
                <Ionicons name="logo-whatsapp" size={14} color={colors.white as string} />
                <Text style={styles.campaignSendSelectedBtnText}>Message {campaignSelectedContacts.size}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        <View style={{ gap: spacing.xs }}>
          {visibleContacts.map(c => {
            const status = campaignActiveStatusTab === 'all' ? getCampaignContactStatus(c.contactId) : campaignActiveStatusTab;
            const cfg = CAMPAIGN_STATUS_CONFIG[status] || { color: colors.text.secondary as string, icon: 'ellipse-outline', label: status };
            const isSelected = campaignSelectedContacts.has(c.contactId);
            return (
              <TouchableOpacity
                key={c.contactId}
                style={[styles.campaignContactRow, isSelected && styles.campaignContactRowSelected]}
                onPress={() => toggleCampaignContact(c.contactId)}
                activeOpacity={0.7}
              >
                <View style={[styles.campaignCheckbox, isSelected && styles.campaignCheckboxChecked]}>
                  {isSelected && <Ionicons name="checkmark" size={12} color={colors.white as string} />}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.campaignContactName} numberOfLines={1}>{c.name || c.phoneNumber || c.contactId}</Text>
                  {c.phoneNumber && c.name && <Text style={styles.campaignContactPhone}>{c.phoneNumber}</Text>}
                </View>
                <View style={[styles.campaignContactBadge, { backgroundColor: cfg.color + '20' }]}>
                  <Ionicons name={cfg.icon as any} size={12} color={cfg.color} />
                  <Text style={[styles.campaignContactBadgeText, { color: cfg.color }]}>{cfg.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
          {visibleContacts.length === 0 && (
            <Text style={styles.campaignContactEmpty}>No contacts in this category</Text>
          )}
        </View>
      </View>
    );
  };

  const renderCampaignActions = () => {
    const task = campaignDetails?.task;
    if (!task) return null;
    const isExecuted = !!(task.lastExecutedAt || task.executionCount > 0);
    const isScheduledNotExecuted = !!(task.nextExecutionAt && new Date(task.nextExecutionAt) > new Date() && !task.lastExecutedAt && task.executionCount === 0);
    const hasContacts = !!(task.parameters?.contacts?.length);

    return (
      <View style={styles.campaignSection}>
        <View style={styles.campaignSectionHeader}>
          <Ionicons name="flash-outline" size={20} color={colors.primary as string} />
          <Text style={styles.campaignSectionTitle}>Actions</Text>
        </View>
        <View style={styles.campaignActionsGrid}>
          {isScheduledNotExecuted && (
            <>
              <TouchableOpacity style={[styles.campaignActionBtn, styles.campaignActionBtnDanger]} onPress={handleCampaignStop} disabled={campaignActionLoading}>
                <Ionicons name="stop-circle-outline" size={16} color="#EF4444" />
                <Text style={[styles.campaignActionBtnText, { color: '#EF4444' }]}>Stop Task</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.campaignActionBtn} onPress={() => setShowCampaignReschedule(true)} disabled={campaignActionLoading}>
                <Ionicons name="calendar-outline" size={16} color={colors.primary as string} />
                <Text style={styles.campaignActionBtnText}>Reschedule</Text>
              </TouchableOpacity>
            </>
          )}
          <TouchableOpacity style={styles.campaignActionBtn} onPress={() => handleCampaignClone(false)}>
            <Ionicons name="copy-outline" size={16} color={colors.primary as string} />
            <Text style={styles.campaignActionBtnText}>Clone Message</Text>
          </TouchableOpacity>
          {hasContacts && (
            <TouchableOpacity style={styles.campaignActionBtn} onPress={() => handleCampaignClone(true)}>
              <Ionicons name="duplicate-outline" size={16} color={colors.primary as string} />
              <Text style={styles.campaignActionBtnText}>Clone + Contacts</Text>
            </TouchableOpacity>
          )}
          {isExecuted && hasContacts && (
            <TouchableOpacity style={[styles.campaignActionBtn, styles.campaignActionBtnPrimary]} onPress={() => handleCampaignClone(true)}>
              <Ionicons name="send-outline" size={16} color={colors.white as string} />
              <Text style={[styles.campaignActionBtnText, { color: colors.white as string }]}>Resend</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderCampaignExecHistory = () => {
    if (executionHistory.length === 0) return (
      <View style={{ padding: spacing.md, alignItems: 'center' }}>
        <Ionicons name="time-outline" size={36} color={colors.text.secondary as string} />
        <Text style={[styles.campaignContactEmpty, { marginTop: spacing.sm }]}>No execution history</Text>
      </View>
    );
    return executionHistory.map((log: any, i: number) => {
      const statusColor = log.status === 'success' ? colors.success : log.status === 'failed' ? '#EF4444' : '#F59E0B';
      const duration = log.duration ? `${(log.duration / 1000).toFixed(1)}s` : 'N/A';
      return (
        <View key={log._id || i} style={styles.campaignExecCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: 2 }}>
            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: statusColor as string }} />
            <Text style={{ fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: statusColor as string, flex: 1 }}>
              {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
            </Text>
            <Text style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary as string }}>{duration}</Text>
            {log.retryAttempt > 0 && <Text style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary as string }}>(Retry #{log.retryAttempt})</Text>}
          </View>
          <Text style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary as string }}>
            {formatDate(log.startedAt)}{log.completedAt ? `  •  ${formatDate(log.completedAt)}` : ''}
          </Text>
          {log.error && (
            <View style={{ marginTop: 4, padding: spacing.xs, backgroundColor: '#EF444415', borderRadius: 4, borderLeftWidth: 2, borderLeftColor: '#EF4444' }}>
              <Text style={{ fontSize: typography.fontSize.xs, color: colors.text.primary as string }}>{log.error}</Text>
            </View>
          )}
        </View>
      );
    });
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={[
          styles.campaignModalContent,
          { maxHeight: Platform.OS !== 'web' ? screenHeight - 120 : undefined, minHeight: Platform.OS !== 'web' ? screenHeight - 120 : undefined, marginTop: Platform.OS !== 'web' ? 120 : undefined },
          Platform.OS === 'web' && isMobileBrowser && styles.campaignModalContentMobileWeb,
          Platform.OS === 'web' && accountForSidePanes && styles.campaignModalContentWeb,
        ]}>
          <View style={styles.campaignModalGrabberWrap}>
            <View style={styles.campaignModalGrabber} />
          </View>

          {/* Header */}
          <View style={styles.campaignModalHeader}>
            <View style={styles.campaignModalHeaderLeft}>
              {campaign && (
                <Ionicons
                  name={
                    campaign.platform?.toLowerCase().includes('whatsapp') ? 'logo-whatsapp' :
                    campaign.platform?.toLowerCase().includes('facebook') || campaign.platform?.toLowerCase().includes('fbm') || campaign.platform?.toLowerCase().includes('messenger') ? 'logo-facebook' :
                    campaign.platform?.toLowerCase().includes('telegram') ? 'paper-plane' :
                    campaign.platform?.toLowerCase().includes('sms') ? 'chatbubble' :
                    'megaphone'
                  }
                  size={24}
                  color={
                    campaign.platform?.toLowerCase().includes('whatsapp') ? '#25D366' :
                    campaign.platform?.toLowerCase().includes('facebook') || campaign.platform?.toLowerCase().includes('fbm') || campaign.platform?.toLowerCase().includes('messenger') ? '#0084FF' :
                    campaign.platform?.toLowerCase().includes('telegram') ? '#0088CC' :
                    campaign.platform?.toLowerCase().includes('sms') ? '#6B7280' :
                    (colors.primary as string)
                  }
                />
              )}
              <View style={styles.campaignModalTitleContainer}>
                <Text style={styles.campaignModalTitle} numberOfLines={1}>
                  {campaign?.name || 'Campaign Details'}
                </Text>
                {campaign && (
                  <Text style={styles.campaignModalSubtitle}>
                    {campaign.platform}
                  </Text>
                )}
              </View>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.campaignModalClose}>
              <Ionicons name="close" size={24} color={colors.text.primary as string} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView
            style={styles.campaignModalBody}
            contentContainerStyle={styles.campaignModalBodyContent}
            showsVerticalScrollIndicator
          >
            {loadingCampaignDetails ? (
              <View style={styles.campaignModalLoading}>
                <ActivityIndicator size="large" color={colors.primary as string} />
                <Text style={styles.campaignModalLoadingText}>Loading details...</Text>
              </View>
            ) : campaignDetails ? (
              <>
                {/* Actions */}
                {renderCampaignActions()}

                {/* Task Details */}
                <View style={styles.campaignSection}>
                  <View style={styles.campaignSectionHeader}>
                    <Ionicons name="document-text-outline" size={20} color={colors.primary as string} />
                    <Text style={styles.campaignSectionTitle}>Task Details</Text>
                  </View>
                  {[
                    { label: 'Status', value: getTaskExecutionStatus(campaignDetails.task).text },
                    { label: 'Type', value: campaignDetails.task.type === 'onDemand' ? 'Immediate' : 'Scheduled' },
                    campaignDetails.task.lastExecutedAt && { label: 'Last Executed', value: formatDate(campaignDetails.task.lastExecutedAt) },
                    campaignDetails.task.nextExecutionAt && { label: 'Next Execution', value: formatDate(campaignDetails.task.nextExecutionAt) },
                    campaignDetails.task.cronExpression && { label: 'Cron', value: campaignDetails.task.cronExpression },
                    campaignDetails.task.parameters?.contacts && { label: 'Contacts', value: `${campaignDetails.task.parameters.contacts.length}${campaignDetails.task.parameters.limitedContacts ? ' (quota limited)' : ''}` },
                    { label: 'Executions', value: `${campaignDetails.task.executionCount} total, ${campaignDetails.task.failureCount} failed` },
                    { label: 'Created', value: formatDate(campaignDetails.task.createdAt) },
                  ].filter(Boolean).map((item: any) => (
                    <View key={item.label} style={styles.campaignInfoItem}>
                      <Text style={styles.campaignInfoLabel}>{item.label}</Text>
                      <Text style={styles.campaignInfoValue}>{item.value}</Text>
                    </View>
                  ))}
                </View>

                {/* Message Statistics */}
                {campaignDetails.stats && (
                  <View style={styles.campaignSection}>
                    <View style={styles.campaignSectionHeader}>
                      <Ionicons name="bar-chart-outline" size={20} color={colors.primary as string} />
                      <Text style={styles.campaignSectionTitle}>Message Statistics</Text>
                      <View style={{ backgroundColor: `${String(colors.success || '#10B981')}20`, paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: 4 }}>
                        <Text style={{ fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: (colors.success as string) || '#10B981' }}>
                          {campaignDetails.stats.successRate}% success
                        </Text>
                      </View>
                    </View>
                    {renderCampaignStatPills(campaignDetails.stats)}
                  </View>
                )}

                {/* Recipient Status */}
                {renderCampaignContactReview()}

                {/* Execution History */}
                <View style={styles.campaignSection}>
                  <View style={styles.campaignSectionHeader}>
                    <Ionicons name="time-outline" size={20} color={colors.primary as string} />
                    <Text style={styles.campaignSectionTitle}>Execution History</Text>
                  </View>
                  {renderCampaignExecHistory()}
                </View>

                {/* Reschedule inline form */}
                {showCampaignReschedule && (
                  <View style={styles.campaignSection}>
                    <Text style={{ fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string, marginBottom: spacing.sm }}>
                      New date &amp; time
                    </Text>
                    <TextInput
                      style={styles.campaignRescheduleInput}
                      value={campaignRescheduleDate}
                      onChangeText={setCampaignRescheduleDate}
                      placeholder={new Date(Date.now() + 3600000).toISOString().slice(0, 16)}
                      placeholderTextColor={colors.text.secondary as string}
                    />
                    <Text style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary as string, marginBottom: spacing.md }}>Format: YYYY-MM-DDTHH:MM</Text>
                    <View style={{ flexDirection: 'row', gap: spacing.md }}>
                      <TouchableOpacity
                        style={[styles.campaignActionBtn, { flex: 1, justifyContent: 'center' }]}
                        onPress={() => { setShowCampaignReschedule(false); setCampaignRescheduleDate(''); }}
                      >
                        <Text style={styles.campaignActionBtnText}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.campaignActionBtn, styles.campaignActionBtnPrimary, { flex: 1, justifyContent: 'center' }]}
                        onPress={handleCampaignReschedule}
                        disabled={campaignActionLoading}
                      >
                        {campaignActionLoading
                          ? <ActivityIndicator size="small" color={colors.white as string} />
                          : <Text style={[styles.campaignActionBtnText, { color: colors.white as string }]}>Confirm</Text>}
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </>
            ) : (
              <View style={styles.campaignModalLoading}>
                <Ionicons name="alert-circle" size={48} color={colors.text.secondary as string} />
                <Text style={styles.campaignModalLoadingText}>No details available</Text>
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2000,
    }),
  },
  campaignModalContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: spacing.borderRadius.xl,
    borderTopRightRadius: spacing.borderRadius.xl,
    maxHeight: '90%',
    minHeight: '50%',
    paddingBottom: Platform.OS === 'ios' ? spacing.xl : spacing.md,
  },
  campaignModalContentMobileWeb: {
    maxWidth: '90%' as any,
    marginLeft: 'auto' as any,
    marginRight: 'auto' as any,
  },
  campaignModalContentWeb: {
    maxWidth: 'calc(100% - 710px)' as any, // Account for left (355px) + right (355px) panes
    marginLeft: '355px' as any,
    marginRight: '355px' as any,
    borderTopLeftRadius: spacing.borderRadius.lg,
    borderTopRightRadius: spacing.borderRadius.lg,
    borderBottomLeftRadius: spacing.borderRadius.lg,
    borderBottomRightRadius: spacing.borderRadius.lg,
  },
  campaignModalGrabberWrap: {
    alignItems: 'center',
    paddingTop: spacing.sm,
  },
  campaignModalGrabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
  },
  campaignModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  campaignModalHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: spacing.sm,
  },
  campaignModalTitleContainer: {
    flex: 1,
  },
  campaignModalTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  campaignModalSubtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.xs,
  },
  campaignModalClose: {
    padding: spacing.xs,
  },
  campaignModalBody: {
    flex: 1,
  },
  campaignModalBodyContent: {
    padding: spacing.md,
  },
  campaignModalLoading: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
  campaignModalLoadingText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.md,
  },
  campaignSection: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  campaignSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  campaignSectionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    flex: 1,
  },
  campaignInfoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 3,
  },
  campaignInfoLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    flex: 1,
  },
  campaignInfoValue: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    flex: 2,
    textAlign: 'right',
  },
  campaignPillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  campaignStatPill: {
    flex: 1,
    minWidth: 70,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    backgroundColor: colors.background.primary,
  },
  campaignStatPillValue: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
  },
  campaignStatPillLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: 2,
  },
  campaignStatusTabs: {
    flexDirection: 'row',
    gap: spacing.xs,
    paddingBottom: spacing.xs,
  },
  campaignStatusTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.primary,
  },
  campaignStatusTabActive: {
    borderColor: colors.primary,
    backgroundColor: `${colors.primary as string}15`,
  },
  campaignStatusTabText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  campaignStatusTabTextActive: {
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  campaignContactActionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    paddingVertical: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  campaignSelectAllRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  campaignSelectAllText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  campaignSendSelectedBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#25D366',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
  },
  campaignSendSelectedBtnText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
  campaignContactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: 'transparent',
    backgroundColor: colors.background.primary,
  },
  campaignContactRowSelected: {
    borderColor: colors.primary,
    backgroundColor: `${colors.primary as string}10`,
  },
  campaignCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.surface,
  },
  campaignCheckboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  campaignContactName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  campaignContactPhone: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  campaignContactBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: spacing.borderRadius.sm,
  },
  campaignContactBadgeText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  campaignContactEmpty: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    textAlign: 'center',
    padding: spacing.md,
  },
  campaignActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  campaignActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: `${colors.primary as string}10`,
  },
  campaignActionBtnPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  campaignActionBtnDanger: {
    borderColor: '#EF4444',
    backgroundColor: '#EF444410',
  },
  campaignActionBtnText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  campaignExecCard: {
    backgroundColor: colors.background.primary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  campaignRescheduleInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
});
